import { Stack } from '@mui/material';
import React, { useCallback } from 'react';
import useTemplates from 'hooks/queries/useTemplates';
import useSenderProfiles from 'hooks/queries/useSenderProfiles';
import {
    CampaignChannels,
    ContentState,
    EditContentPayload,
    EditMetadataPayload,
    MetadataState,
} from 'domains/campaigns/types';
import { SenderProfile } from 'domains/content/types';
import ContentSelector from 'domains/campaigns/components/CustomCampaign/components/ContentSelector';
import Select from 'domains/core/components/Select';
import TextField from 'domains/core/components/TextField';
import InputLabel from 'domains/core/components/InputLabel';

type Props = {
    contentIndex: number;
    contentState: ContentState;
    metadataState: MetadataState;
    editContent: (payload: EditContentPayload) => void;
    editMetadata: (payload: EditMetadataPayload) => void;
};

const CurationContentForm = ({ contentIndex, contentState, metadataState, editContent, editMetadata }: Props) => {
    const { preheader, senderProfileId, subjectLine, templateId } = contentState;

    const { isLoading: isSenderProfilesLoading, data: senderProfiles = [] } = useSenderProfiles();
    const { data: templates = [], isLoading: isLoadingTemplates } = useTemplates();

    const handleContentChange = useCallback(
        (contentId: number | null) => {
            if (contentId) {
                // Find the selected template to get all its properties
                const template = templates.find((t) => t.id === contentId);
                if (template) {
                    editContent({
                        ...contentState,
                        templateId: contentId,
                        senderProfileId: template.senderProfileId,
                        subjectLine: template.subjectLine ?? '',
                        preheader: template.preHeader ?? '',
                    });
                }
            } else {
                // Clear all fields when no template selected
                editContent({
                    ...contentState,
                    templateId: null,
                    senderProfileId: null,
                    subjectLine: '',
                    preheader: '',
                });

                // Clear preview if showing current content
                if (metadataState.previewContentId === contentIndex) {
                    editMetadata({ previewContentId: null });
                }
            }
        },
        [contentIndex, contentState, editContent, editMetadata, metadataState.previewContentId, templates]
    );

    const handlePreviewChange = useCallback(() => {
        if (contentIndex === metadataState.previewContentId) {
            editMetadata({ previewContentId: null });
        } else {
            editMetadata({ previewContentId: contentIndex });
        }
    }, [contentIndex, editMetadata, metadataState.previewContentId]);

    return (
        <Stack rowGap={4}>
            <ContentSelector
                contentType={CampaignChannels.EMAIL}
                contentId={templateId}
                contentList={templates}
                onContentChange={handleContentChange}
                isPreviewVisible={metadataState.previewContentId === contentIndex}
                onPreviewChange={handlePreviewChange}
                isLoading={isLoadingTemplates}
            />
            {/* If the user clears the templateId from the Autocomplete, we don't want to hide the other fields if they have filled them out. */}
            <>
                <Select
                    data-test="curation-content-page-sender-profile-dropdown"
                    data-testid="curation-content-page-sender-profile-dropdown"
                    required
                    label={<InputLabel label="sender profile" labelIconTooltipText="Who is sending this email?" />}
                    menuItemStyles={{ textTransform: 'none' }}
                    onChange={(e: any) => {
                        editContent({ ...contentState, senderProfileId: e.target.value as number });
                    }}
                    value={senderProfileId ?? ''}
                    values={
                        !isSenderProfilesLoading
                            ? senderProfiles?.map((senderProfile: SenderProfile) => ({
                                  value: senderProfile.id,
                                  label: senderProfile.from_address,
                              }))
                            : []
                    }
                    fullWidth
                />
                <TextField
                    counterCurrent={subjectLine?.length ?? 0}
                    counterMax={255}
                    data-test="curation-content-page-subject-line"
                    data-testid="curation-content-page-subject-line"
                    fullWidth
                    inputProps={{ maxLength: 255 }}
                    label={
                        <InputLabel
                            label="subject line"
                            labelIconTooltipText="A descriptive overview of your message that gives contacts a reason to open."
                        />
                    }
                    onChange={(e: any) => {
                        editContent({ ...contentState, subjectLine: e.target.value });
                    }}
                    value={subjectLine ?? ''}
                    withCounter={true}
                />
                <TextField
                    counterCurrent={preheader?.length ?? 0}
                    counterMax={100}
                    data-test="curation-content-page-preview"
                    data-testid="curation-content-page-preview"
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                    label={
                        <InputLabel
                            label="preview text"
                            labelIconTooltipText="A quick glimpse into the content of your message that appears after the subject line."
                        />
                    }
                    onChange={(e: any) => {
                        editContent({ ...contentState, preheader: e.target.value });
                    }}
                    value={preheader ?? ''}
                    withCounter={true}
                />
            </>
        </Stack>
    );
};

export default CurationContentForm;
